import { createStandaloneToast } from '@chakra-ui/react';
import moment from 'moment';
import Big from 'big.js';
import axios from 'axios';

import { ChainId } from '../config/constants';
import { ToastProps } from './types';

const tokenURI = process.env.REACT_APP_STORE_URI;
const metaPrefix = process.env.REACT_APP_METADATA_PREFIX;
const configURI = process.env.REACT_APP_CONFIG_URI;
const graphQLNFTURI = process.env.REACT_APP_GRAPHQL_NFT_URI;
const graphQLVESTINGURI = process.env.REACT_APP_GRAPHQL_VESTING_URI;
const graphQLRiceURI = process.env.REACT_APP_GRAPHQL_RICE_URI;

export const ETHERSCAN_PREFIXES: { [chainId: number]: string } = {
  1: '',
  3: 'ropsten.',
  4: 'rinkeby.',
  5: 'goerli.',
  42: 'kovan.',
  97: 'bsctestnet.',
  100: 'xdai',
};

export function getEtherscanLink(
  chainId: ChainId,
  data: string,
  type: 'transaction' | 'token' | 'address' | 'block',
): string {
  const prefix = `https://${ETHERSCAN_PREFIXES[chainId] || ETHERSCAN_PREFIXES[1]}etherscan.io`;

  switch (type) {
    case 'transaction': {
      return `${prefix}/tx/${data}`;
    }
    case 'token': {
      return `${prefix}/token/${data}`;
    }
    case 'block': {
      return `${prefix}/block/${data}`;
    }
    case 'address':
    default: {
      return `${prefix}/address/${data}`;
    }
  }
}

const toastStandalone = createStandaloneToast();
export const toast = ({
  title = '',
  desc = '',
  status = 'success',
  duration = 9000,
  isClosable = true,
}: ToastProps) => {
  toastStandalone({
    position: 'bottom-right',
    title,
    description: desc,
    status,
    duration,
    isClosable,
  });
};

export const getNowTimestamp = () => {
  return new Date().getTime();
};

export const timeformater = (timestamp: number, format = 'YYYY-MM-DD HH:mm:ss') => {
  const time = Number(timestamp);
  if (time === 0) return '';
  return moment(time * 1000)
    .utc()
    .format(format);
};

export const formatNumber = (num: number) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const formatBalance = (amount = '0', decimal = 18) => {
  const num = Big(amount).div(10 ** decimal);
  return num.toFixed(8);
};

export const getTestVestingNFTData = (id: any) => {
  // 10K: 2400~2499  20K: 2600~2659  50K: 2800~2829 100K: 3000~3029
  // const idNum = Number(id);
  // let metadataUrl = `${id}`;
  // if (idNum >= 1600 && idNum <= 1659) {
  //   metadataUrl = `10k-test`;
  // } else if (idNum >= 1800 && idNum <= 1859) {
  //   metadataUrl = `20k-test`;
  // } else if (idNum >= 2000 && idNum <= 2029) {
  //   metadataUrl = `50k-test`;
  // } else if (idNum >= 2200 && idNum <= 2229) {
  //   metadataUrl = `100k-test`;
  // } else {
  //   metadataUrl = `1`;
  // }

  return `${tokenURI}/${metaPrefix}/vesting.json`;
};

export const getMetadata = async (cardId: string, uri = tokenURI) => {
  try {
    const url = getTestVestingNFTData(cardId) || `${uri}/${metaPrefix}/1.json`;

    const req = await axios.get(url);
    const { data } = req;
    return {
      cardId,
      ...data,
      id: cardId,
    };
  } catch (error) {
    console.log('get metadata error: ', error);
    return {};
  }
};

export const getConfig = async (fileName: string, uri = configURI) => {
  try {
    const url = `${uri}/${fileName}.json`;
    const req = await axios.get(url);
    const { data } = req;
    return data;
  } catch (error) {
    console.log('get config file error: ', error);
    return {};
  }
};

export const postQuery = async (query: string, params = {}) => {
  try {
    const url = graphQLNFTURI || '';
    const req = await axios.post(url, { query, variables: params });
    const { data } = req;
    return data.data;
  } catch (error) {
    console.log('get owner data error: ', error);
    return {};
  }
};

export const postQueryNFTs = async (query: string, params = {}) => {
  try {
    const url = graphQLNFTURI || '';
    const req = await axios.post(url, { query, variables: params });
    const { data } = req;
    return data.data;
  } catch (error) {
    console.log('get owner data error: ', error);
    return {};
  }
};

export const postQueryVesting = async (query: string, params = {}) => {
  try {
    const url = graphQLVESTINGURI || '';
    const req = await axios.post(url, { query, variables: params });
    const { data } = req;
    return data.data;
  } catch (error) {
    console.log('get owner data error: ', error);
    return {};
  }
};

export const postQueryRice = async (query: string, params = {}) => {
  try {
    const url = graphQLRiceURI || '';
    const req = await axios.post(url, { query, variables: params });
    const { data } = req;
    return data.data;
  } catch (error) {
    console.log('get mainnet rice data error: ', error);
    return {};
  }
};

export const toFixed = (number = 0, decimals = 8, isAddZero = false, isRounding = true) => {
  const preNum = number;
  if (Number.isNaN(preNum) || (!preNum && preNum !== 0)) {
    return '';
  }

  // 先进行四舍五入
  let num = 0;
  if (isRounding === true) {
    const decimalsNumTemp = 10 ** decimals; // 取保留的小数最大整数值 如10 100 1000等
    num = Math.round(parseFloat(preNum) * decimalsNumTemp) / decimalsNumTemp;
  }

  // 默认为保留的小数点后两位
  const tempNum = Number(num);
  const pointIndex = String(tempNum).indexOf('.') + 1; // 获取小数点的位置 + 1
  const pointCount = pointIndex ? String(tempNum).length - pointIndex + 1 : 0; // 获取小数点后的个数(需要保证有小数位)
  // 小数位小于需要截取的位数
  if (pointIndex === 0 || pointCount <= decimals) {
    // 源数据为整数或者小数点后面小于decimals位的作补零处理
    if (isAddZero === true) {
      let tempNumA = tempNum;
      if (pointIndex === 0) {
        tempNumA = `${tempNumA}.`;
        for (let index = 0; index < decimals - pointCount; index += 1) {
          tempNumA = `${tempNumA}0`;
        }
      } else {
        for (let index = 0; index < decimals - pointCount; index += 1) {
          tempNumA = `${tempNumA}0`;
        }
      }
      return tempNumA;
    }

    return tempNum;
  }
  let realVal = '';

  // 截取当前数据到小数点后decimals位
  realVal = `${String(tempNum).split('.')[0]}.${String(tempNum)
    .split('.')[1]
    .substring(0, decimals)}`;

  // 判断截取之后数据的数值是否为0
  if (realVal === 0) {
    realVal = 0;
  }
  return realVal;
};
